import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  List,
  message,
  Modal,
  Tooltip,
  Transfer,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import api from '../../services/api';
import { CadastroPage } from './CadastroPage';
import { listarGruposV2, PAGE_SIZE } from '../../services/cadastrosV2';

export function GruposUsuarioV2() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(1), [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarGruposV2(debouncedSearch);
      setItems(response?.data || []);
    } catch (error) {
      setItems([]);
      message.error(
        error?.response?.data?.detail || 'Não foi possível carregar os grupos.'
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const paginatedItems = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page]
  );

  const openNew = async () => {
    setEditing(null);
    setTargetKeys([]);
    form.resetFields();
    setOpen(true);
    setSaving(true);
    try {
      const response = await api.get('/Role/list');
      setRoles(response?.data || []);
    } catch (error) {
      message.error('Não foi possível carregar as permissões.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async item => {
    setOpen(true);
    setSaving(true);
    try {
      const response = await api.get(`/GrupoUsuario/${item.id}`);
      const group = response?.data;
      const linked = group?.rolesVinculadas || [];
      const available = group?.rolesDisponiveis || [];
      setEditing(group);
      setRoles([...available, ...linked]);
      setTargetKeys(linked.map(role => role.nome));
      form.setFieldsValue({ nome: group.nome });
    } catch (error) {
      message.error('Não foi possível carregar os dados do grupo.');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    setRoles([]);
    setTargetKeys([]);
    form.resetFields();
  };

  const save = async values => {
    setSaving(true);
    const selectedRoles = roles.filter(role => targetKeys.includes(role.nome));
    const payload = {
      id: editing?.id,
      nome: values.nome.trim(),
      roles: selectedRoles,
    };
    try {
      if (editing) await api.put('/GrupoUsuario', payload);
      else await api.post('/GrupoUsuario', payload);
      message.success(
        `Grupo ${editing ? 'atualizado' : 'cadastrado'} com sucesso.`
      );
      close();
      await load();
    } catch (error) {
      message.error(
        error?.response?.data?.detail || 'Não foi possível salvar o grupo.'
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = item => {
    Modal.confirm({
      title: `Excluir o grupo “${item.nome}”?`,
      content: 'Esta ação não pode ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete(`/GrupoUsuario/${item.id}`);
          message.success('Grupo excluído com sucesso.');
          if (paginatedItems.length === 1 && page > 1) setPage(page - 1);
          await load();
        } catch (error) {
          message.error(
            error?.response?.data?.detail || 'Não foi possível excluir o grupo.'
          );
        }
      },
    });
  };

  return (
    <>
      <CadastroPage
        title="Grupos de usuário"
        newLabel="Novo grupo"
        search={search}
        onSearch={setSearch}
        loading={loading}
        items={paginatedItems}
        total={items.length}
        page={page}
        onPageChange={setPage}
        onNew={openNew}
        renderItem={item => (
          <List.Item
            key={item.id}
            actions={[
              <Button
                key="edit"
                type="link"
                icon={<EditOutlined />}
                onClick={() => openEdit(item)}
              >
                Editar
              </Button>,
              <Button
                key="delete"
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(item)}
              >
                Excluir
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={item.nome}
              description="Grupo de permissões de acesso"
            />
          </List.Item>
        )}
      />

      <Modal
        open={open}
        title={editing ? 'Editar grupo de usuário' : 'Novo grupo de usuário'}
        footer={null}
        width={900}
        maskClosable={false}
        destroyOnHidden
        onCancel={close}
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item
            name="nome"
            label="Nome do grupo"
            rules={[{ required: true, message: 'Informe o nome do grupo.' }]}
          >
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item label="Permissões">
            <Transfer
              className="cadastros-v2-transfer"
              dataSource={roles.map(role => ({
                ...role,
                key: role.nome,
                title: role.nome,
              }))}
              targetKeys={targetKeys}
              onChange={setTargetKeys}
              render={role => (
                <Tooltip
                  title={role.descricao || role.nome}
                  placement="topLeft"
                >
                  <span className="cadastros-v2-role-name">{role.nome}</span>
                </Tooltip>
              )}
              titles={['Disponíveis', 'Permissões do grupo']}
              showSearch
              style={{ width: '100%' }}
              listStyle={{
                flex: '1 1 0',
                width: 0,
                minWidth: 0,
                height: 400,
              }}
            />
          </Form.Item>
          <div className="cadastros-v2-modal-actions">
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Salvar
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
